import {
  logEvent,
  getExperiment as statsigGetExperiment,
} from './util.statsig.js';

export const PURCHASE_INTENT_STORAGE_KEY = 'jjb_membership_purchase_intent';
// 历史命名带 embedded，但 HOSTED 换卡/赠转付回跳恢复仍依赖该 key，不能改名或删除。
export const CHECKOUT_ID_STORAGE_KEY = 'jjb_membership_embedded_checkout_id';
// 独立成功页读取该 key 核验 Checkout。
const SUCCESS_CHECKOUT_ID_STORAGE_KEY = 'jjb_membership_checkout_id';
export const CHECKOUT_TERMINAL_STATES = [
  'COMPLETED',
  'APPLIED',
  'REFUNDED',
  'CANCELLED',
  'EXPIRED',
  'FAILED',
];

export function normalizeEntryPage(value) {
  return value || 'direct';
}

export function normalizeMembershipState(state) {
  const value = state && typeof state === 'object' ? state : {};
  return {
    plan: String(value.plan || 'BASIC').toUpperCase(),
    status: String(
      value.membershipStatus || value.status || 'BASIC',
    ).toUpperCase(),
    billingStatus: String(value.billingStatus || '').toUpperCase(),
    membershipSource: value.membershipSource || 'PAID',
    giftId: value.giftId || null,
    giftEndsAt: value.giftEndsAt || null,
    resumeVisible: value.resumeVisible,
    renewalStatus: value.renewalStatus || null,
    paidThrough: value.paidThrough || value.currentPeriodEndsAt || '',
    billingCycle: value.billingCycle || value.billing_cycle || '',
  };
}

export function requiresPaymentMethodUpdate(state) {
  const membership = normalizeMembershipState(state);
  return (
    membership.status === 'DUNNING' ||
    (membership.billingStatus === 'UNPAID' &&
      ['ACTIVE', 'CANCELLING'].includes(membership.status))
  );
}

export function isActivePaidMembership(state, now = Date.now()) {
  const membership = normalizeMembershipState(state);
  if (membership.membershipSource === 'GIFT') return false;
  if (membership.plan !== 'PRO' && membership.plan !== 'PREMIUM') return false;
  if (membership.status === 'ACTIVE') return true;
  if (
    membership.status !== 'CANCELLING' &&
    !requiresPaymentMethodUpdate(membership)
  )
    return false;
  const paidThrough = Date.parse(membership.paidThrough);
  return Number.isFinite(paidThrough) && paidThrough > now;
}

export function getGenerationTaskCount(data) {
  if (data?.allTotalTasksNum === null || data?.allTotalTasksNum === undefined) {
    return null;
  }
  const count = Number(data?.allTotalTasksNum);
  return Number.isFinite(count) && count >= 0 ? count : null;
}

export function hasRequiredGenerationCount(data) {
  return (getGenerationTaskCount(data) ?? 0) > 10;
}

export function createPurchaseIntent({
  entryPage,
  returnUrl,
  now = Date.now(),
}) {
  const safeReturnUrl = String(returnUrl || '/');
  return {
    version: 1,
    entry_page: normalizeEntryPage(entryPage),
    plan_type: 'pro',
    billing_cycle: 'monthly',
    return_url:
      safeReturnUrl.startsWith('/') && !safeReturnUrl.startsWith('//')
        ? safeReturnUrl
        : '/',
    created_at: now,
    expires_at: now + 10 * 60 * 1000,
  };
}

export function parsePurchaseIntent(raw, { now = Date.now() } = {}) {
  let value;
  try {
    value = typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch {
    return null;
  }
  if (
    !value ||
    value.version !== 1 ||
    value.expires_at <= now ||
    value.created_at > now
  )
    return null;
  if (value.plan_type !== 'pro' || value.billing_cycle !== 'monthly')
    return null;
  if (
    typeof value.return_url !== 'string' ||
    !value.return_url.startsWith('/') ||
    value.return_url.startsWith('//')
  ) {
    return null;
  }
  return { ...value, entry_page: normalizeEntryPage(value.entry_page) };
}

export function resolveCheckoutState(status) {
  const value = String(status || '').toUpperCase();
  if (value === 'COMPLETED' || value === 'APPLIED') return 'payment_success';
  if (CHECKOUT_TERMINAL_STATES.includes(value)) return 'payment_failed';
  return 'confirming';
}

function checkoutFailureMessage(status, fallback) {
  return String(status || '').toUpperCase() === 'REFUNDED'
    ? 'Your payment was refunded.'
    : fallback;
}

export function createIdempotencyKey() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `membership-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

export function isMembershipPopupBackdropClick(target, currentTarget) {
  return target === currentTarget;
}

const EXPERIMENT_NAME = 'promotion_popup';
const HIDE_GIFT_BOX_EXPERIMENT_NAME = 'hide_gift_box';
const PROMOTION_POPUP_GROUP_IDS = { old: 31, new: 32, none: 33 };
const HIDE_GIFT_BOX_GROUP_IDS = { false: 35, true: 36 };
const STORAGE_KEY_PREFIX = 'newsletter_popup_';
const CHECKOUT_CACHE_TTL_MS = 5 * 60 * 1000;
const CHECKOUT_SOURCE_STORAGE_KEY_PREFIX = 'jjb_membership_checkout_source_';

function isTestMode() {
  return window.__RELEASE__.isTest;
}

function getExperiment(experimentName) {
  if (isTestMode() && window.jjbUtils?.overrideExperiment) {
    return window.jjbUtils.overrideExperiment(experimentName);
  }
  return statsigGetExperiment(experimentName);
}

function getMembershipApiPrefix() {
  return `${isTestMode() ? '/apps/ai-test' : '/apps/ai'}/membership`;
}

function getApiPrefix() {
  return isTestMode() ? '/apps/ai-test' : '/apps/ai';
}

async function requestMembership(path, options = {}) {
  const response = await fetch(getMembershipApiPrefix() + path, {
    method: options.method || 'GET',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...(options.headers || {}),
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
  });
  options.onResponse?.({ traceId: response.headers.get('X-Trace-Id') });
  const payload = await response.json().catch(() => null);
  if (!response.ok || !payload || payload.success === false) {
    const error = new Error(
      payload?.message || payload?.errorMessage || 'Membership request failed.',
    );
    error.code = payload?.code;
    error.status = response.status;
    throw error;
  }
  return payload.data;
}

async function hasRequiredGenerationCountForAutoOpen() {
  let identity = {};
  try {
    identity = window.jjbUtils?.getIdentity?.() || {};
  } catch {}
  const params = new URLSearchParams({ activityType: 'canvas', pageSize: '1' });
  Object.entries(identity).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });
  try {
    const response = await fetch(
      `${getApiPrefix()}/activity/tasks?${params.toString()}`,
      {
        method: 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      },
    );
    const payload = await response.json().catch(() => null);
    if (!response.ok || !payload || payload.success === false) return false;
    return hasRequiredGenerationCount(payload.data);
  } catch {
    return false;
  }
}

async function mergeAnonymousActivityAfterLogin() {
  let identity = {};
  try {
    identity = window.jjbUtils?.getIdentity?.() || {};
  } catch {}
  const customerId = identity.customerId ? String(identity.customerId) : '';
  const anonymousId = identity.anonymousId ? String(identity.anonymousId) : '';
  if (!customerId || !anonymousId) return;
  const startedAt = performance.now();
  let outcome = 'success';
  try {
    // 与 Checkout 一致的超时兜底：请求永久 pending 时不再悬挂埋点收口。
    const response = await checkoutTimeout(
      fetch(`${getApiPrefix()}/activity/tasks/merge`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ customerId, anonymousId }),
      }),
      'activity merge',
    );
    const payload = await response.json().catch(() => null);
    if (!response.ok || !payload || payload.success === false) {
      throw new Error(
        payload?.message || payload?.errorMessage || 'Activity merge failed.',
      );
    }
  } catch (error) {
    outcome = 'error';
    console.warn('[jjb-membership-popup] login activity merge failed', error);
  } finally {
    try {
      await trackMembership(
        'membership_activity_merge',
        { outcome, duration_ms: Math.round(performance.now() - startedAt) },
        { monitor: true, ga4: false },
      );
      Promise.resolve(window.zlog?.flushTracking?.()).catch(() => {});
    } catch {}
  }
}

function showCheckoutRequestError(error) {
  if (typeof window.toast !== 'function') return;
  if (error?.status === 400 && String(error.code) === '1027') {
    window.toast(
      'Your membership information is syncing. Please refresh the page.',
      10000,
    );
    return;
  }
  if (error?.status === 429) {
    window.toast(
      'Please refresh the page and complete the human verification process.',
      10000,
    );
    return;
  }
  if (error?.status >= 500 && error.status <= 599) {
    window.toast(
      `${error.message || 'Unable to start secure checkout.'} Please take a screenshot and contact customer support.`,
      10000,
    );
  }
}

function trackMembership(eventName, params = {}, options = {}) {
  return window?.zlog?.track?.(
    eventName,
    { page: window.location.pathname, ...params },
    { statsig: false, monitor: false, ...options },
  );
}

function getAirwallexPaymentUrl(data) {
  return data?.url || '';
}

function getAirwallexErrorParams(error, checkoutId) {
  return {
    checkout_id: checkoutId || undefined,
    error_code: error?.code || error?.errorCode || undefined,
    error_message: error?.message || String(error || 'Unknown Airwallex error'),
  };
}

function getThemeMembershipState() {
  return (
    window.theme?.membershipState ||
    window.JJB_CUSTOMER_MEMBERSHIP_STATE ||
    null
  );
}

function isLoggedIn() {
  return Boolean(window.theme?.customerId);
}

function trackCheckoutMonitor(eventName, params = {}) {
  return trackMembership(
    eventName,
    { customerId: window.theme?.customerId || null, ...params },
    { monitor: true },
  );
}

function getSafeReturnUrl() {
  return (
    window.location.pathname + window.location.search + window.location.hash
  );
}

function createCheckoutSourceHash() {
  if (typeof window.crypto?.randomUUID === 'function') {
    return `h_${window.crypto.randomUUID()}`;
  }
  return `h_${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
}

function storeCheckoutSource(source) {
  const hash = createCheckoutSourceHash();
  try {
    window.localStorage.setItem(
      CHECKOUT_SOURCE_STORAGE_KEY_PREFIX + hash,
      source,
    );
  } catch {}
  return hash;
}

function getLoginUrl(returnUrl) {
  return `/customer_authentication/login?return_to=${encodeURIComponent(returnUrl)}`;
}

export function getMembershipCheckoutShopOrigin() {
  const origin = String(window.location?.origin || '');
  if (origin === 'https://jujubit.ai') {
    return 'https://exn11x-id.myshopify.com';
  }
  if (origin === 'https://jujubit-dev.myshopify.com') {
    return 'https://jujubit-dev.myshopify.com';
  }
  return origin;
}

export function getCheckoutSuccessUrl(planType = 'pro') {
  const url = new URL(
    '/pages/checkout-succeed',
    getMembershipCheckoutShopOrigin(),
  );
  url.searchParams.set('source', storeCheckoutSource(getSafeReturnUrl()));
  url.searchParams.set('plan', planType === 'premium' ? 'PREMIUM' : 'PRO');
  return url.href;
}

function checkoutTimeout(promise, phase, timeoutMs) {
  // e2e 可通过 window.jjbMembershipRequestTimeoutMs 缩短超时窗口；生产默认 20s。
  const timeout =
    timeoutMs ??
    ((typeof window !== 'undefined' &&
      Number(window.jjbMembershipRequestTimeoutMs)) ||
      20_000);
  let timer;
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      timer = window.setTimeout(
        () =>
          reject(new Error(`Payment ${phase} timed out. Please try again.`)),
        timeout,
      );
    }),
  ]).finally(() => window.clearTimeout(timer));
}

function createCheckoutAttempt(uiMode, source, startedAt) {
  return (
    window.jjbCheckoutRuntime?.createAttempt?.(uiMode, source, startedAt) || {
      bind() {},
      record() {},
      finish() {},
      async measure(_phase, operation) {
        return operation();
      },
    }
  );
}

export function createMembershipPopupController() {
  // 独立成功页自行核验 Checkout；弹窗控制器不能抢先消费并清除回跳参数。
  if (document.querySelector('[data-jjb-vip-checkout]')) return null;
  const root = document.querySelector('[data-jjb-membership-popup]');
  if (!root) return null;
  let offer = root.querySelector('[data-jjb-membership-offer]');
  let paywallVisualReady = null;
  function resolvePaywallVisualOffer() {
    if (paywallVisualReady) return paywallVisualReady;
    if (!window.jjbUtils?.resolveMembershipPaywallVisual) return null;
    paywallVisualReady = Promise.resolve(
      window.jjbUtils.resolveMembershipPaywallVisual(),
    )
      .catch(() => ({ variant: 'legacy' }))
      .then((visual) => {
        const variant = visual?.variant || 'legacy';
        let active = window.jjbUtils?.applyMembershipPaywallVisual?.(
          root,
          variant,
        );
        if (!active) {
          root.dataset.membershipVisualVariant = variant;
          root
            .querySelectorAll('[data-membership-visual-variant]')
            .forEach((container) => {
              const selected =
                container.dataset.membershipVisualVariant === variant;
              container.hidden = !selected;
              container.inert = !selected;
              if (selected) {
                container.removeAttribute('aria-hidden');
                active = container;
              } else {
                container.setAttribute('aria-hidden', 'true');
              }
            });
        }
        offer =
          active?.querySelector('[data-jjb-membership-offer]') ||
          root.querySelector('[data-jjb-membership-offer]');
        return offer;
      });
    return paywallVisualReady;
  }
  const checkout = root.querySelector('[data-jjb-membership-checkout]');
  const resultDialog = root.querySelector('[data-jjb-membership-result]');
  const checkoutLoading = checkout.querySelector(
    '[data-jjb-membership-checkout-loading]',
  );
  const status = checkout.querySelector('[data-jjb-membership-checkout-status]');
  const retry = checkout.querySelector('[data-jjb-membership-checkout-retry]');
  const checkoutClose = checkout.querySelector(
    '[data-jjb-membership-checkout-close]',
  );
  const checkoutTitle = checkout.querySelector('#jjb-membership-checkout-title');
  const resultMessage = root.querySelector(
    '[data-jjb-membership-result-message]',
  );
  const resultRetry = root.querySelector('[data-jjb-membership-result-retry]');
  const resultClose = root.querySelector('[data-jjb-membership-result-close]');

  let context = null;
  let latestPaymentError = null;
  let checkoutState = 'closed';
  let checkoutRecovery = null;
  let activeRequest = 0;
  let abortController = null;
  let idempotencyKey = '';
  let checkoutIntentExpiresAt = 0;
  let checkoutAttempt;
  let startingCheckout = false;
  let previousFocus = null;
  let hasOpened = false;
  let pollingTimer;
  let pollingStartedAt = 0;
  let successCloseTimer;
  const fallbackCheckoutIntents = new Map();
  const designMode = root.dataset.designMode === 'true';
  const popupSectionId = root.dataset.popupSectionId || '';
  const popupDelayDays = getDelayDays(root);
  const CTA_LOADING_PAINT_DELAY_MS = 120;
  let hiddenShopifyChat = [];
  let ctaLoading = false;
  function hideShopifyChat() {
    if (hiddenShopifyChat.length) return;
    hiddenShopifyChat = [...document.querySelectorAll('shopify-chat')].map(
      (element) => {
        const display = element.style.display;
        element.style.display = 'none';
        return { element, display };
      },
    );
  }

  function restoreShopifyChat() {
    hiddenShopifyChat.forEach(({ element, display }) => {
      element.style.display = display;
    });
    hiddenShopifyChat = [];
  }

  function normalizeCheckoutBillingCycle(value) {
    const normalized = String(value || '')
      .trim()
      .toLowerCase();
    return normalized === 'year' || normalized === 'yearly'
      ? 'yearly'
      : 'monthly';
  }

  function checkoutContext(options = {}) {
    return {
      entry_page: normalizeEntryPage(options.entry_page),
      plan_type: String(options.plan_type || 'pro').toLowerCase(),
      billing_cycle: normalizeCheckoutBillingCycle(options.billing_cycle),
      plan_label:
        options.plan_label ||
        (options.plan_type === 'premium'
          ? 'Premium Membership'
          : 'Pro Membership'),
      display_price: String(
        options.display_price || root.dataset.defaultCheckoutPrice || '',
      ),
      action_type: options.action_type || 'purchase',
      purpose: ['SETUP', 'GIFT_RESUME'].includes(options.purpose)
        ? options.purpose
        : 'PURCHASE',
      giftId: options.giftId || null,
      signupId: options.signupId || null,
      scheduledStartsAt: options.scheduledStartsAt || null,
    };
  }

  function isGiftResume() {
    return context?.purpose === 'GIFT_RESUME';
  }
  function isSetupCheckout() {
    return context?.purpose === 'SETUP' || isGiftResume();
  }

  function giftCheckoutData(data) {
    context.scheduledStartsAt = data.scheduledStartsAt;
    context.plan_type = data.targetPlan === 'PREMIUM' ? 'premium' : 'pro';
    context.billing_cycle =
      data.targetBillingCycle === 'YEAR' ? 'yearly' : 'monthly';
    context.plan_label =
      data.targetPlan === 'PREMIUM' ? 'Premium Membership' : 'Pro Membership';
    return {
      ...data,
      id: data.signupId,
      mode: 'GIFT_RESUME',
      clientSecret: data.checkout?.clientSecret || null,
      // HOSTED 会话唯一可跳转凭据；下线前遗留的页内会话为 null。
      url: data.checkout?.url || null,
      status: ['SUBSCRIPTION_SCHEDULED', 'ACTIVATED'].includes(data.status)
        ? 'COMPLETED'
        : ['FAILED', 'CANCELLED'].includes(data.status)
          ? 'CANCELLED'
          : data.checkout?.clientSecret || data.checkout?.url
            ? 'ACTIVE'
            : 'PROCESSING',
    };
  }

  async function readCheckout(id, options) {
    const data = await requestMembership(
      isGiftResume()
        ? `/gift/signups/${encodeURIComponent(id)}`
        : `/checkouts/${encodeURIComponent(id)}`,
      options,
    );
    return isGiftResume() ? giftCheckoutData(data) : data;
  }

  function applyCheckoutMode(data) {
    // 浏览器缓存只提供恢复线索，服务端会话 mode 才决定换卡或购买语义。
    if (data?.mode === 'GIFT_RESUME') {
      context.purpose = 'GIFT_RESUME';
      context.scheduledStartsAt = data.scheduledStartsAt;
      syncCheckoutHeader();
      return;
    }
    const purpose =
      String(data?.mode || '').toUpperCase() === 'SETUP' ? 'SETUP' : 'PURCHASE';
    context.purpose = purpose;
    if (purpose === 'SETUP') context.action_type = 'payment_method_setup';
    else if (context.action_type === 'payment_method_setup')
      context.action_type = 'purchase';
    syncCheckoutHeader();
  }

  async function refreshCheckoutMembership(signal) {
    const customerId = String(window.theme?.customerId || '');
    const state = await checkoutTimeout(
      requestMembership('/me', { signal }),
      'membership status',
    );
    if (signal?.aborted)
      throw new DOMException('Checkout closed.', 'AbortError');
    if (customerId !== String(window.theme?.customerId || ''))
      throw new Error(
        'Your customer account has changed. Please reopen checkout.',
      );
    if (!state || typeof state !== 'object')
      throw new Error('Unable to check membership status. Please try again.');
    if (window.theme) window.theme.membershipState = state;
    window.JJB_CUSTOMER_MEMBERSHIP_STATE = state;
    return normalizeMembershipState(state);
  }

  function processingMessage() {
    if (isGiftResume()) return 'Confirming your scheduled subscription...';
    return isSetupCheckout()
      ? 'Updating your payment method...'
      : 'Processing your payment...';
  }

  async function completeCheckoutResult(data, restored = false) {
    applyCheckoutMode(data);
    retireCheckoutIntent(idempotencyKey);
    checkoutAttempt?.finish('completed');
    setStoredCheckoutId('');
    checkoutState = 'payment_success';
    root.dataset.checkoutState = checkoutState;
    if (isGiftResume()) {
      showResult(
        'success',
        '<strong>Subscription scheduled.</strong> Your subscription starts on ' +
          new Date(context.scheduledStartsAt).toLocaleString() +
          '. Your gift membership stays active until then.',
      );
      window.dispatchEvent(new CustomEvent('jjb:membership-updated'));
      void refreshCheckoutMembership().catch(() => {});
      return;
    }
    if (isSetupCheckout()) {
      showResult(
        'success',
        '<strong>Payment method updated.</strong> Your membership will refresh after the renewal payment is confirmed.',
      );
      window.dispatchEvent(new CustomEvent('jjb:membership-updated'));
      // 换卡完成不等于续费入账，只读取当前权益，不在前端恢复套餐。
      void refreshCheckoutMembership().catch(() => {});
      return;
    }
    showResult(
      'success',
      restored
        ? 'This payment was confirmed. Your current membership details will refresh.'
        : `Congrats! <strong>You're now on ${getResultPlanLabel(data?.planLabel)}!</strong>`,
    );
    scheduleSuccessReload();
    if (!restored) await fireConfetti();
  }

  function visibleDialog() {
    if (!resultDialog.hidden) return resultDialog;
    return checkout.hidden
      ? root.querySelector('[data-jjb-membership-popup-view]:not([hidden])')
      : checkout;
  }

  function syncView() {
    const mobile = window.matchMedia('(max-width: 767px)').matches;
    offer
      .querySelectorAll('[data-jjb-membership-popup-view]')
      .forEach((view) => {
        view.hidden =
          view.dataset.jjbMembershipPopupView === 'mobile' ? !mobile : mobile;
      });
  }

  function lockPage(locked) {
    document.documentElement.style.overflow = locked ? 'hidden' : '';
    document.body.style.overflow = locked ? 'hidden' : '';
  }

  function setCheckoutState(nextState, message) {
    checkoutState = nextState;
    root.dataset.checkoutState = nextState;
    status.innerHTML = message || '';
    if (checkoutClose)
      checkoutClose.disabled =
        nextState === 'submitting' || nextState === 'confirming';
    retry.hidden =
      nextState !== 'init_error' && nextState !== 'confirm_timeout';
    retry.textContent =
      nextState === 'confirm_timeout' ? 'Check status' : 'Try again';
    checkoutLoading.hidden = nextState !== 'checkout_loading';
  }

  function showResult(state, message, retryLabel = '') {
    checkout.hidden = true;
    offer.hidden = true;
    resultDialog.hidden = false;
    resultDialog.dataset.resultState = state;
    resultMessage.innerHTML = typeof message === 'string' ? message : '';
    resultRetry.hidden = !retryLabel;
    resultRetry.textContent = retryLabel;
    resultClose.disabled =
      state === 'processing' && checkoutState !== 'confirm_timeout';
    focusDialog();
  }

  function showRefundedCheckout() {
    setStoredCheckoutId('');
    setCheckoutState('payment_failed', '');
    showResult('failed', 'Your payment was refunded.', 'Retry');
  }

  function getResultPlanLabel(label) {
    const fallback = context.plan_type === 'premium' ? 'Premium' : 'Pro';
    return String(label || fallback).replace(/\s+Membership$/i, '');
  }

  async function waitForConfetti() {
    const startedAt = Date.now();
    while (Date.now() - startedAt < 3000) {
      if (typeof window.confetti === 'function') return window.confetti;
      await new Promise((resolve) => window.setTimeout(resolve, 50));
    }
    return null;
  }

  async function fireConfetti() {
    const confetti = await waitForConfetti();
    if (!confetti) return;
    try {
      confetti({
        disableForReducedMotion: true,
        origin: { x: 0.5, y: 0.72 },
        particleCount: 100,
        spread: 90,
        startVelocity: 48,
        zIndex: 10000,
      });
    } catch {}
  }

  function focusDialog() {
    window.requestAnimationFrame(() => {
      const dialog = visibleDialog();
      const focusable = dialog?.querySelector(
        'button:not([disabled]), a[href], input, [tabindex="0"]',
      );
      focusable?.focus({ preventScroll: true });
    });
  }

  function openLayer() {
    // 本页已选择会员窗口后，迟到的自动分流不能再打开会员或营销弹窗。
    hasOpened = true;
    previousFocus = document.activeElement;
    syncView();
    root.hidden = false;
    hideShopifyChat();
    lockPage(true);
    focusDialog();
  }

  function clearPolling() {
    if (pollingTimer) window.clearTimeout(pollingTimer);
    pollingTimer = undefined;
  }

  function clearSuccessCloseTimer() {
    if (successCloseTimer) window.clearTimeout(successCloseTimer);
    successCloseTimer = undefined;
  }

  function scheduleSuccessReload() {
    clearSuccessCloseTimer();
    successCloseTimer = window.setTimeout(() => {
      successCloseTimer = undefined;
      window.location.reload();
    }, 4000);
  }

  function closeLayer() {
    const confirmationTimedOut = checkoutState === 'confirm_timeout';
    const storedCheckout = getStoredCheckout();
    const keepUnconfirmedCheckout =
      storedCheckout?.pendingConfirmation === true ||
      ['SETUP', 'GIFT_RESUME'].includes(storedCheckout?.context?.purpose);
    if (
      checkoutState === 'submitting' ||
      checkoutState === 'confirming' ||
      (!resultDialog.hidden &&
        resultDialog.dataset.resultState === 'processing' &&
        !confirmationTimedOut)
    )
      return;
    checkoutAttempt?.finish('closed');
    startingCheckout = false;
    const shouldStartPopupCooldown = context?.entry_page === 'launch';
    activeRequest += 1;
    abortController?.abort();
    abortController = null;
    clearPolling();
    if (checkoutState !== 'payment_success') clearSuccessCloseTimer();
    // 手动关闭会员弹窗后清理登录回跳意图并重置 CTA，避免下次入口继承 loading 状态。
    clearPurchaseIntent();
    setCtaLoading(false);
    checkout.hidden = true;
    resultDialog.hidden = true;
    offer.hidden = false;
    root.hidden = true;
    root.removeAttribute('data-checkout-state');
    lockPage(false);
    restoreShopifyChat();
    checkoutState = 'closed';
    context = null;
    idempotencyKey = '';
    // 已提交但未确认的付款必须保留原会话；普通关闭仍清理未提交的恢复标记。
    if (!keepUnconfirmedCheckout) setStoredCheckoutId('');
    previousFocus?.focus?.({ preventScroll: true });
    previousFocus = null;
    if (shouldStartPopupCooldown && popupSectionId) {
      markPopupClosed(popupSectionId);
    }
  }

  async function getAnalytics(entryPage = context?.entry_page) {
    try {
      const analytics =
        await window.jjbCheckoutRuntime?.getAnalytics?.(entryPage);
      if (analytics) return analytics;
    } catch {}
    return entryPage ? { entryPage } : {};
  }

  function prewarm() {
    if (designMode) return;
    try {
      window.jjbCheckoutRuntime?.prepareAnalytics?.();
    } catch {}
  }

  function canReplaceEndedCheckout(data) {
    return data?.status === 'CANCELLED' || data?.status === 'EXPIRED';
  }

  function retireCheckoutIntent(key) {
    window.jjbCheckoutRuntime?.retireIntent?.(key);
    for (const [scope, intent] of fallbackCheckoutIntents) {
      if (intent.idempotencyKey === key) fallbackCheckoutIntents.delete(scope);
    }
  }

  async function checkoutBody(
    requestId,
    attempt,
    current = { ...context },
    assignIntent = true,
  ) {
    const analytics = await attempt.measure('analytics', () =>
      getAnalytics(current.entry_page),
    );
    if (requestId !== null && requestId !== activeRequest)
      throw new DOMException('Checkout closed.', 'AbortError');
    const plan = current.plan_type === 'premium' ? 'PREMIUM' : 'PRO';
    const billingCycle = current.billing_cycle === 'yearly' ? 'YEAR' : 'MONTH';
    // 会员收银台只保留 HOSTED 跳转；uiMode 固定显式传 HOSTED，旧后端不会
    // 回落到页内会话，新后端忽略该字段。
    const uiMode = 'HOSTED';
    const intentOptions = {
      plan,
      billingCycle,
      uiMode,
      purpose: current.purpose,
      giftId: current.giftId,
      environment: isTestMode() ? 'test' : 'prod',
      analytics,
      createSuccessUrl: () => getCheckoutSuccessUrl(current.plan_type),
    };
    let intent = window.jjbCheckoutRuntime?.getIntent?.(intentOptions);
    if (!intent) {
      // 共享脚本加载失败时仍可付款；同页同意图重试保持原请求身份。
      let consent;
      try {
        consent = window.Shopify?.customerPrivacy?.currentVisitorConsent?.();
      } catch {}
      const scope = JSON.stringify(
        current.purpose === 'GIFT_RESUME'
          ? [
              window.theme?.customerId,
              intentOptions.environment,
              'GIFT_RESUME',
              current.giftId,
              plan,
              billingCycle,
              uiMode,
            ]
          : current.purpose === 'SETUP'
            ? [
                window.theme?.customerId,
                intentOptions.environment,
                consent,
                'SETUP',
                uiMode,
              ]
            : [
                window.theme?.customerId,
                intentOptions.environment,
                consent,
                plan,
                billingCycle,
                uiMode,
                current.entry_page,
                window.location.href,
              ],
      );
      const now = Date.now();
      for (const [key, value] of fallbackCheckoutIntents) {
        if (value.expires <= now) fallbackCheckoutIntents.delete(key);
      }
      intent = fallbackCheckoutIntents.get(scope);
      if (!intent) {
        intent = {
          idempotencyKey:
            window.crypto?.randomUUID?.() || createCheckoutSourceHash(),
          successUrl: intentOptions.createSuccessUrl(),
          analytics,
          expires: ['SETUP', 'GIFT_RESUME'].includes(current.purpose)
            ? Number.MAX_SAFE_INTEGER
            : now + CHECKOUT_CACHE_TTL_MS,
        };
        fallbackCheckoutIntents.set(scope, intent);
      }
    }
    if (assignIntent) {
      idempotencyKey = intent.idempotencyKey;
      checkoutIntentExpiresAt = Number(intent.expires) || 0;
    }
    return {
      plan,
      billingCycle,
      uiMode,
      successUrl: intent.successUrl,
      idempotencyKey: intent.idempotencyKey,
      analytics: intent.analytics,
    };
  }

  async function startCheckout() {
    if (
      startingCheckout ||
      ['checkout_loading', 'submitting', 'confirming'].includes(checkoutState)
    )
      return false;
    const startToken = {};
    startingCheckout = startToken;
    const startedAt = performance.now();
    setCtaLoading(true);
    try {
      return await initializeCheckout(startedAt);
    } finally {
      if (startingCheckout === startToken) {
        if (!root.hidden) setCtaLoading(false);
        startingCheckout = false;
      }
    }
  }

  function storePurchaseIntent() {
    const intent = createPurchaseIntent({
      entryPage: context.entry_page,
      returnUrl: getSafeReturnUrl(),
    });
    try {
      sessionStorage.setItem(
        PURCHASE_INTENT_STORAGE_KEY,
        JSON.stringify(intent),
      );
    } catch {}
    setCtaLoading(false);
    window.location.href = getLoginUrl(intent.return_url);
  }

  /**
   * 购买意图只在终态清除：用户显式关闭弹窗、已是付费会员不再恢复，
   * 或 Checkout 已成功创建（此后刷新由 checkout 恢复缓存或幂等回放兜底）。
   * 中途失败/超时/刷新时保留意图，保证登录回流可安全重试。
   */
  function clearPurchaseIntent() {
    try {
      sessionStorage.removeItem(PURCHASE_INTENT_STORAGE_KEY);
    } catch {}
  }

  function setCtaLoading(loading) {
    ctaLoading = loading;
    root.querySelectorAll('[data-jjb-membership-join]').forEach((button) => {
      if (!button.dataset.membershipIdleHtml) {
        button.dataset.membershipIdleHtml = button.innerHTML;
      }
      button.disabled = loading;
      button.setAttribute('aria-busy', loading ? 'true' : 'false');
      button.classList.toggle('is-loading', loading);
      button.innerHTML = loading
        ? '<span class="jjb-membership-offer__cta-spinner" aria-hidden="true"></span><span>Loading...</span>'
        : button.dataset.membershipIdleHtml;
    });
  }

  function waitForCtaLoadingPaint() {
    return new Promise((resolve) =>
      window.setTimeout(resolve, CTA_LOADING_PAINT_DELAY_MS),
    );
  }

  function getStoredCheckout() {
    const customerId = String(window.theme?.customerId || '');
    let cached =
      checkoutRecovery?.customerId === customerId ? checkoutRecovery : null;
    try {
      if (!cached)
        cached = JSON.parse(sessionStorage.getItem(CHECKOUT_ID_STORAGE_KEY));
      const checkoutId = String(cached?.checkoutId || '');
      const requestedAt = Number(cached?.requestedAt || 0);
      if (
        !checkoutId ||
        (cached.customerId && cached.customerId !== customerId) ||
        (context &&
          cached.context &&
          cached.pendingConfirmation !== true &&
          !['SETUP', 'GIFT_RESUME'].includes(cached.context.purpose) &&
          (cached.context.plan_type !== context.plan_type ||
            cached.context.billing_cycle !== context.billing_cycle)) ||
        !Number.isFinite(requestedAt) ||
        requestedAt <= 0 ||
        (cached.pendingConfirmation !== true &&
          !['SETUP', 'GIFT_RESUME'].includes(cached.context?.purpose) &&
          checkoutState !== 'confirm_timeout' &&
          Date.now() - requestedAt > CHECKOUT_CACHE_TTL_MS)
      ) {
        setStoredCheckoutId('');
        return null;
      }
      if (
        cached.pendingConfirmation === true ||
        ['SETUP', 'GIFT_RESUME'].includes(cached.context?.purpose)
      )
        checkoutRecovery = cached;
      return cached;
    } catch {
      setStoredCheckoutId('');
      return null;
    }
  }

  function getStoredCheckoutId() {
    return String(getStoredCheckout()?.checkoutId || '');
  }

  function setStoredCheckoutId(checkoutId, pendingConfirmation = false) {
    const cached = checkoutId
      ? {
          checkoutId,
          requestedAt: Date.now(),
          customerId: String(window.theme?.customerId || ''),
          context,
          idempotencyKey,
          intentExpiresAt: checkoutIntentExpiresAt,
          ...(pendingConfirmation ? { pendingConfirmation: true } : {}),
        }
      : null;
    // 同页恢复还要覆盖Storage被浏览器拒绝的普通未提交表单。
    checkoutRecovery = cached;
    try {
      if (cached)
        sessionStorage.setItem(CHECKOUT_ID_STORAGE_KEY, JSON.stringify(cached));
      else sessionStorage.removeItem(CHECKOUT_ID_STORAGE_KEY);
    } catch {}
  }

  async function pollCheckout(
    checkoutId,
    requestId = activeRequest,
    restored = false,
  ) {
    if (requestId !== activeRequest || root.hidden) return;
    clearPolling();
    if (!pollingStartedAt) pollingStartedAt = Date.now();
    const queryAbort = new AbortController();
    abortController?.abort();
    abortController = queryAbort;
    try {
      const result = await checkoutTimeout(
        readCheckout(checkoutId, {
          signal: queryAbort.signal,
        }),
        'confirmation',
        Math.max(1, 60_000 - (Date.now() - pollingStartedAt)),
      );
      if (requestId !== activeRequest || root.hidden) return;
      applyCheckoutMode(result);
      const nextState = resolveCheckoutState(result?.status);
      if (nextState === 'payment_success') {
        await completeCheckoutResult(result, restored);
        return;
      }
      if (nextState === 'payment_failed') {
        retireCheckoutIntent(idempotencyKey);
        checkoutAttempt?.finish('terminal');
        latestPaymentError = {
          code: result?.code,
          message: result?.message || 'Payment failed. Please try again.',
        };
        setStoredCheckoutId('');
        checkoutState = 'payment_failed';
        root.dataset.checkoutState = checkoutState;
        showResult(
          'failed',
          checkoutFailureMessage(
            result?.status,
            'Payment failed. Please try again.',
          ),
          'Retry',
        );
        return;
      }
      if (Date.now() - pollingStartedAt >= 60_000) {
        checkoutState = 'confirm_timeout';
        root.dataset.checkoutState = checkoutState;
        showResult(
          'processing',
          'Payment is still being confirmed.',
          'Check status',
        );
        return;
      }
      const delay = Date.now() - pollingStartedAt < 10_000 ? 1000 : 2500;
      pollingTimer = window.setTimeout(
        () => pollCheckout(checkoutId, requestId, restored),
        delay,
      );
    } catch {
      if (requestId !== activeRequest || root.hidden) return;
      if (Date.now() - pollingStartedAt >= 60_000) {
        checkoutState = 'confirm_timeout';
        root.dataset.checkoutState = checkoutState;
        showResult(
          'processing',
          'We could not confirm the result yet.',
          'Check status',
        );
      } else {
        pollingTimer = window.setTimeout(
          () => pollCheckout(checkoutId, requestId, restored),
          2500,
        );
      }
    } finally {
      queryAbort.abort();
      if (abortController === queryAbort) abortController = null;
    }
  }

  function syncCheckoutHeader() {
    // 弹窗只承载 loading/错误/重试，仅同步标题。
    if (!checkoutTitle) return;
    checkoutTitle.textContent = isGiftResume()
      ? 'Resume membership'
      : isSetupCheckout()
        ? 'Update payment method'
        : 'Confirm Membership';
  }

  async function restoreCheckout(
    checkoutId,
    startedAt = performance.now(),
    { interactive = false } = {},
  ) {
    const stored = getStoredCheckout();
    const pendingConfirmation =
      stored?.checkoutId === checkoutId && stored.pendingConfirmation === true;
    if (stored?.checkoutId === checkoutId) {
      context = { ...context, ...stored.context };
      idempotencyKey = stored.idempotencyKey || '';
      checkoutIntentExpiresAt = Number(stored.intentExpiresAt) || 0;
    }
    const requestId = ++activeRequest;
    checkoutAttempt?.finish('closed');
    const attempt = (checkoutAttempt = createCheckoutAttempt(
      'HOSTED',
      'restore',
      startedAt,
    ));
    abortController?.abort();
    const restoreAbort = new AbortController();
    abortController = restoreAbort;
    syncCheckoutHeader();
    resultDialog.hidden = true;
    checkout.hidden = false;
    offer.hidden = true;
    setCheckoutState(
      'checkout_loading',
      '<div><span class="jjb-membership-spinner" aria-hidden="true"></span><p>Loading secure checkout...</p></div>',
    );
    try {
      const data = await attempt.measure('restore', () =>
        checkoutTimeout(
          readCheckout(checkoutId, {
            signal: restoreAbort.signal,
            onResponse: (fields) => attempt.bind(fields),
          }),
          'restore',
        ),
      );
      if (requestId !== activeRequest || checkout.hidden) return;
      applyCheckoutMode(data);
      setStoredCheckoutId(checkoutId, pendingConfirmation);
      const nextState = resolveCheckoutState(data?.status);
      if (
        isGiftResume() &&
        !interactive &&
        !pendingConfirmation &&
        nextState !== 'payment_success' &&
        nextState !== 'payment_failed'
      ) {
        // 自动恢复（页面加载/bfcache 返回）不得唤起支付：不跳收银台，
        // 只清除恢复上下文并刷新会员状态；重试入口留给
        // 用户点击 Resume/Complete setup（interactive 路径）。先清存储再关层：
        // closeLayer 对赠转付目的会保留未确认会话标记。
        setStoredCheckoutId('');
        if (!root.hidden) {
          closeLayer();
        } else {
          checkoutAttempt?.finish('closed');
          checkoutState = 'closed';
          root.dataset.checkoutState = checkoutState;
        }
        window.dispatchEvent(new CustomEvent('jjb:membership-updated'));
        return;
      }
      // 其余赠转付恢复结果（成功/失败/确认）与手势重试需要弹层
      // 承接；换卡 SETUP 同理：手势重试与结果态（成功/失败/确认中）补开弹层，
      // 自动恢复的未提交会话走下方静默清理，不唤起支付。
      const giftRedirectingToCashier =
        isGiftResume() && !pendingConfirmation && interactive && data.url;
      const setupNeedsLayer =
        context?.purpose === 'SETUP' &&
        (interactive ||
          pendingConfirmation ||
          nextState === 'payment_success' ||
          nextState === 'payment_failed');
      if (
        root.hidden &&
        ((isGiftResume() && !giftRedirectingToCashier) || setupNeedsLayer)
      )
        openLayer();
      if (nextState === 'payment_success') {
        await completeCheckoutResult(data, true);
        return;
      }
      if (nextState === 'payment_failed') {
        retireCheckoutIntent(idempotencyKey);
        checkoutAttempt?.finish('terminal');
        latestPaymentError = {
          code: data?.code,
          message: data?.message || 'Payment failed. Please try again.',
        };
        setStoredCheckoutId('');
        checkoutState = 'payment_failed';
        root.dataset.checkoutState = checkoutState;
        showResult(
          'failed',
          checkoutFailureMessage(
            data?.status,
            'Payment failed. Please try again.',
          ),
          'Retry',
        );
        return;
      }
      if (isGiftResume() && !pendingConfirmation && interactive && data.url) {
        // 用户主动重试的 HOSTED 收银台会话：重新跳转继续付款，
        // 不能落入确认轮询（会话未支付，轮询永远不会完成）。
        setStoredCheckoutId(checkoutId, pendingConfirmation);
        window.location.href = data.url;
        return;
      }
      if (context?.purpose === 'SETUP' && !pendingConfirmation) {
        if (interactive && data.url) {
          // 用户主动重试的 HOSTED 换卡会话：跳转收银台继续付款。
          setStoredCheckoutId(checkoutId, pendingConfirmation);
          window.location.href = data.url;
          return;
        }
        if (!interactive) {
          // 自动恢复（页面加载/bfcache）不唤起支付：未提交/确认中的换卡会话
          // 一律静默清理退出（与赠转付规则一致），不空转确认轮询；
          // 重试入口留给用户手势路径。
          setStoredCheckoutId('');
          if (!root.hidden) {
            closeLayer();
          } else {
            checkoutAttempt?.finish('closed');
            checkoutState = 'closed';
            root.dataset.checkoutState = checkoutState;
          }
          window.dispatchEvent(new CustomEvent('jjb:membership-updated'));
          return;
        }
      }
      if (
        String(data?.status || '').toUpperCase() === 'ACTIVE' &&
        !pendingConfirmation
      ) {
        if (!isSetupCheckout()) {
          const membership = await refreshCheckoutMembership(
            restoreAbort.signal,
          );
          if (requestId !== activeRequest || checkout.hidden) return;
          if (requiresPaymentMethodUpdate(membership)) {
            // 旧页面保存的未提交购买不能绕过当前欠费分流，修卡使用独立意图。
            setStoredCheckoutId('');
            context.purpose = 'SETUP';
            context.action_type = 'payment_method_setup';
            checkoutState = 'closed';
            await initializeCheckout(startedAt);
            return;
          }
        }
        // 未提交的 HOSTED 购买会话：有 url 时由用户手势重新跳转继续付款。
        if (interactive && /^https:\/\//i.test(String(data?.url || ''))) {
          window.location.href = data.url;
          return;
        }
        // 页内支付已下线：下线前遗留的 ACTIVE 会话（只有 clientSecret、没有 url）
        // 不再挂载表单，清理本地 checkoutId，让用户重新发起 HOSTED。
        // 静默恢复不展示任何内容；用户手势恢复展示可重试的错误。
        setStoredCheckoutId('');
        if (!interactive) {
          checkoutAttempt?.finish('closed');
          if (!root.hidden) closeLayer();
          else {
            checkoutState = 'closed';
            root.dataset.checkoutState = checkoutState;
          }
          return;
        }
        throw new Error('Checkout is not ready.');
      }
      setStoredCheckoutId(checkoutId, true);
      checkoutState = 'confirming';
      root.dataset.checkoutState = checkoutState;
      showResult('processing', processingMessage());
      pollingStartedAt = 0;
      pollCheckout(checkoutId, activeRequest, true);
    } catch (error) {
      if (requestId !== activeRequest) return;
      attempt.finish('error');
      // 手势驱动的恢复：读会话失败也要让用户看到结果（错误/Check status），
      // 不能在从未打开的隐藏层里静默展示。
      if (interactive && root.hidden) openLayer();
      if (
        getStoredCheckoutId() &&
        (pendingConfirmation ||
          isSetupCheckout() ||
          (stored?.intentExpiresAt > 0 && stored.intentExpiresAt <= Date.now()))
      ) {
        checkoutState = 'confirm_timeout';
        root.dataset.checkoutState = checkoutState;
        showResult(
          'processing',
          'We could not confirm the result yet.',
          'Check status',
        );
        return;
      }
      setCheckoutState(
        'init_error',
        `<div>${error?.message || 'Unable to restore checkout.'}</div>`,
      );
    } finally {
      restoreAbort.abort();
      if (abortController === restoreAbort) abortController = null;
    }
  }

  async function initializeCheckout(startedAt = performance.now()) {
    if (
      checkoutState === 'checkout_loading' ||
      checkoutState === 'submitting' ||
      checkoutState === 'confirming'
    )
      return;
    // 购买/升级走 HOSTED 整页跳转。换卡 SETUP 与赠转付必须走本函数主流程
    //（会员校验 + POST + url 跳转）：redirectToAirwallexCheckout 对 SETUP
    // 一律委托回这里，此分支不得再转回去，否则互相递归。
    if (!isSetupCheckout()) {
      return redirectToAirwallexCheckout({ source: 'new' });
    }
    const storedCheckout = getStoredCheckout();
    // 换挡：赠转付点击的档位/周期与存储会话不一致时，旧意向让位走新建
    // （后端会取消旧意向建立新意向）；View 入口（带 signupId）不点击具体
    // 档位，保持恢复既有签约。
    if (
      isGiftResume() &&
      !context.signupId &&
      storedCheckout &&
      (storedCheckout.context?.plan_type !== context.plan_type ||
        storedCheckout.context?.billing_cycle !== context.billing_cycle)
    ) {
      setStoredCheckoutId('');
    }
    const storedCheckoutId =
      getStoredCheckoutId() || (isGiftResume() ? context.signupId : null);
    if (storedCheckoutId) {
      // 点击驱动的恢复允许重新唤起支付（跳收银台）。
      return restoreCheckout(storedCheckoutId, startedAt, {
        interactive: true,
      });
    }
    const requestId = ++activeRequest;
    let attempt;
    latestPaymentError = null;
    abortController?.abort();
    const requestAbort = (abortController = new AbortController());
    const guard = () => {
      if (requestId !== activeRequest || requestAbort.signal.aborted)
        throw new DOMException('Checkout closed.', 'AbortError');
    };
    offer.hidden = true;
    resultDialog.hidden = true;
    checkout.hidden = false;
    syncCheckoutHeader();
    setCheckoutState(
      'checkout_loading',
      '<div><span class="jjb-membership-spinner" aria-hidden="true"></span><p>Loading secure checkout...</p></div>',
    );
    let checkoutResponseValidated = false;
    let checkoutRefunded = false;
    try {
      const membership = await refreshCheckoutMembership(requestAbort.signal);
      guard();
      if (isGiftResume() || membership.membershipSource === 'GIFT') {
        if (
          membership.membershipSource !== 'GIFT' ||
          !membership.giftEndsAt ||
          Date.parse(membership.giftEndsAt) <= Date.now() ||
          membership.resumeVisible === false
        )
          throw new Error(
            'Your gift membership has changed. Please refresh your membership.',
          );
        context.purpose = 'GIFT_RESUME';
        context.giftId = membership.giftId;
        context.scheduledStartsAt = membership.giftEndsAt;
      }
      if (
        !isGiftResume() &&
        isSetupCheckout() &&
        !requiresPaymentMethodUpdate(membership)
      )
        throw new Error(
          'Your membership status has changed. Please review your membership before continuing.',
        );
      if (!isGiftResume())
        context.purpose = requiresPaymentMethodUpdate(membership)
          ? 'SETUP'
          : 'PURCHASE';
      if (isSetupCheckout()) context.action_type = 'payment_method_setup';
      // 会员状态刷新后可能不再是换卡/赠转付，改走购买 HOSTED 跳转。
      if (!isSetupCheckout()) {
        checkout.hidden = true;
        checkoutState = 'closed';
        root.dataset.checkoutState = checkoutState;
        return redirectToAirwallexCheckout({ source: 'new' });
      }
      syncCheckoutHeader();
      attempt = checkoutAttempt = createCheckoutAttempt(
        'HOSTED',
        'new',
        startedAt,
      );
      if (!isSetupCheckout())
        trackCheckoutMonitor('membership_popup_checkout_start');
      let body = await checkoutBody(requestId, attempt);
      guard();
      const data = await attempt.measure('backend', async () => {
        let replacedEndedCheckout = false;
        const replaceEndedIntent = async () => {
          guard();
          retireCheckoutIntent(body.idempotencyKey);
          body = await checkoutBody(requestId, attempt);
          guard();
          replacedEndedCheckout = true;
        };
        const create = async () => {
          guard();
          return checkoutTimeout(
            requestMembership(
              isGiftResume()
                ? '/gift/resume'
                : isSetupCheckout()
                  ? '/subscription/payment-method/setup'
                  : '/checkouts',
              {
                method: 'POST',
                signal: requestAbort.signal,
                headers: { 'Idempotency-Key': body.idempotencyKey },
                onResponse: (fields) => attempt.bind(fields),
                ...(isGiftResume()
                  ? {
                      body: {
                        plan: body.plan,
                        billingCycle: body.billingCycle,
                        idempotencyKey: body.idempotencyKey,
                        uiMode: 'HOSTED',
                      },
                    }
                  : isSetupCheckout()
                    ? {
                        body: { uiMode: 'HOSTED' },
                      }
                    : { body }),
              },
            ),
            'checkout creation',
          );
        };
        let response = await create();
        if (isGiftResume()) response = giftCheckoutData(response);
        guard();
        if (!replacedEndedCheckout && canReplaceEndedCheckout(response)) {
          await replaceEndedIntent();
          response = await create();
          if (isGiftResume()) response = giftCheckoutData(response);
          guard();
        }
        return response;
      });
      if (requestId !== activeRequest) return;
      applyCheckoutMode(data);
      // HOSTED（换卡/赠转付）跳转只在"非成功/非终态且带 url"时发生；
      // COMPLETED/CANCELLED 的 hosted 会话仍带 url 但不会跳转——成功/终态
      // 结果必须在可见弹层内展示，不能因响应带 url 就跳过开层。
      const setupRedirectingToCashier =
        isSetupCheckout() &&
        Boolean(data.url) &&
        resolveCheckoutState(data?.status) !== 'payment_success' &&
        !CHECKOUT_TERMINAL_STATES.includes(data?.status);
      if (
        (isGiftResume() || context?.purpose === 'SETUP') &&
        root.hidden &&
        !setupRedirectingToCashier
      )
        openLayer();
      if (
        isSetupCheckout() &&
        resolveCheckoutState(data?.status) === 'payment_success'
      ) {
        await completeCheckoutResult(data);
        return;
      }
      if (CHECKOUT_TERMINAL_STATES.includes(data?.status)) {
        checkoutRefunded = data.status === 'REFUNDED';
        retireCheckoutIntent(idempotencyKey);
        throw new Error(
          checkoutFailureMessage(
            data?.status,
            'Checkout has ended. Please try again.',
          ),
        );
      }
      const checkoutData = {
        ...data,
        checkoutId: data?.id || data?.checkoutId,
      };
      if (isSetupCheckout() && data.url) {
        // HOSTED 收银台会话（换卡/赠转付）：整页跳转继续付款。
        checkoutResponseValidated = true;
        clearPurchaseIntent();
        setStoredCheckoutId(checkoutData.checkoutId);
        attempt.bind({
          checkoutId: data.checkout?.id || checkoutData.checkoutId,
        });
        window.location.href = data.url;
        return;
      }
      if (
        checkoutData.checkoutId &&
        (data.status === 'PROCESSING' ||
          (isSetupCheckout() && !data.url && data.status !== 'ACTIVE'))
      ) {
        // 原付款已受理而权益仍在应用时，只确认原ID。
        // 未提交的 ACTIVE 会话缺少 url 时不得进入确认轮询，落到下方报错重试。
        setStoredCheckoutId(checkoutData.checkoutId, true);
        clearPurchaseIntent();
        checkoutState = 'confirming';
        root.dataset.checkoutState = checkoutState;
        showResult('processing', processingMessage());
        pollingStartedAt = 0;
        void pollCheckout(checkoutData.checkoutId);
        return;
      }
      throw new Error('Checkout is not ready.');
    } catch (error) {
      if (error?.name === 'AbortError' || requestId !== activeRequest) return;
      attempt?.finish('error');
      // 本函数所有入口均为手势驱动。HOSTED 换卡/赠转付未预开弹层，
      // 创建请求失败时也要让用户看到错误与重试入口。
      if (root.hidden) openLayer();
      if (!checkoutResponseValidated && !isSetupCheckout()) {
        trackCheckoutMonitor('membership_popup_checkout_fail', {
          message: error?.message,
        });
      }
      showCheckoutRequestError(error);
      if (checkoutRefunded) {
        showRefundedCheckout();
      } else {
        setCheckoutState(
          'init_error',
          `<div>${error?.message || 'Unable to initialize checkout.'}</div>`,
        );
      }
    }
  }

  function showPopup(options, showOffer) {
    const entryPage = context.entry_page;
    openLayer();
    offer.hidden = !showOffer;
    checkout.hidden = true;
    resultDialog.hidden = true;
    if (showOffer) {
      trackMembership(
        'membership_plan_impression',
        {
          entry_page: entryPage,
          style: 'popup',
        },
        { statsig: true },
      );
    }
  }

  function open(options = {}, { showOffer = true } = {}) {
    if (
      startingCheckout ||
      checkoutState === 'checkout_loading' ||
      checkoutState === 'submitting' ||
      checkoutState === 'confirming'
    ) {
      return true;
    }
    context = checkoutContext(options);
    prewarm();
    const visualOffer = showOffer ? resolvePaywallVisualOffer() : null;
    if (visualOffer) {
      void visualOffer.then(() => showPopup(options, showOffer));
    } else {
      showPopup(options, showOffer);
    }
    return true;
  }

  function openCheckout(options = {}) {
    if (designMode) {
      open(options);
      return Promise.resolve(true);
    }
    if (
      startingCheckout ||
      ['checkout_loading', 'submitting', 'confirming'].includes(checkoutState)
    )
      return Promise.resolve(true);
    // 会员收银台只保留 HOSTED：不预开弹窗，当前页按钮 loading，准备好后跳转。
    context = checkoutContext(options);
    context.action_type =
      options.action_type ||
      (isActivePaidMembership(getThemeMembershipState())
        ? 'upgrade'
        : 'purchase');
    return Promise.resolve(startCheckout()).then(
      () => true,
      () => true,
    );
  }

  root.querySelectorAll('[data-jjb-membership-close]').forEach((button) => {
    button.addEventListener('click', () => {
      const variant = button.closest('[data-membership-visual-variant]');
      if (
        variant &&
        variant.dataset.membershipVisualVariant !==
          root.dataset.membershipVisualVariant
      )
        return;
      closeLayer();
    });
  });
  root.querySelectorAll('[data-jjb-membership-offer]').forEach((offerRoot) => {
    offerRoot.addEventListener('click', (event) => {
      if (isMembershipPopupBackdropClick(event.target, event.currentTarget)) {
        closeLayer();
      }
    });
  });
  root.querySelectorAll('[data-jjb-membership-join]').forEach((button) => {
    button.addEventListener('click', async () => {
      const variant = button.closest('[data-membership-visual-variant]');
      if (
        variant &&
        variant.dataset.membershipVisualVariant !==
          root.dataset.membershipVisualVariant
      )
        return;
      if (ctaLoading) return;
      setCtaLoading(true);
      trackMembership(
        'membership_popup_click',
        {
          entry_page: context?.entry_page,
          plan_type: 'Pro',
          billing_cycle: 'monthly',
          isLogin: isLoggedIn(),
        },
        { monitor: true, statsig: true },
      );
      await waitForCtaLoadingPaint();
      if (!isLoggedIn()) {
        storePurchaseIntent();
        return;
      }
      await startCheckout();
    });
  });
  resultClose.addEventListener('click', closeLayer);
  function setCheckoutRetryLoading(loading) {
    retry.disabled = loading;
    retry.setAttribute('aria-busy', loading ? 'true' : 'false');
    retry.innerHTML = loading
      ? '<span class="jjb-membership-checkout__retry-spinner" aria-hidden="true"></span><span>Try again</span>'
      : 'Try again';
  }

  function setResultRetryLoading(loading) {
    resultRetry.disabled = loading;
    resultRetry.setAttribute('aria-busy', loading ? 'true' : 'false');
    resultRetry.innerHTML = loading
      ? '<span class="jjb-membership-result__retry-spinner" aria-hidden="true"></span><span>Retry</span>'
      : 'Retry';
  }

  async function redirectToAirwallexCheckout({
    keepCheckoutError = false,
    source = 'fallback',
  } = {}) {
    if (!context) return false;
    if (isSetupCheckout()) {
      checkoutState = 'closed';
      await initializeCheckout();
      return false;
    }
    const requestId = ++activeRequest;
    const attempt = createCheckoutAttempt('HOSTED', source);
    checkoutAttempt = attempt;
    abortController?.abort();
    abortController = new AbortController();
    setStoredCheckoutId('');
    let checkoutRefunded = false;
    try {
      const membership = await refreshCheckoutMembership(
        abortController.signal,
      );
      if (requestId !== activeRequest) return false;
      if (
        membership.membershipSource === 'GIFT' ||
        requiresPaymentMethodUpdate(membership)
      ) {
        context.purpose =
          membership.membershipSource === 'GIFT' ? 'GIFT_RESUME' : 'SETUP';
        context.action_type = 'payment_method_setup';
        checkoutState = 'closed';
        await initializeCheckout();
        return false;
      }
      trackCheckoutMonitor('membership_popup_checkout_start');
      const body = await checkoutBody(requestId, attempt);
      const data = await attempt.measure('backend', () =>
        checkoutTimeout(
          requestMembership('/checkouts', {
            method: 'POST',
            signal: abortController.signal,
            headers: { 'Idempotency-Key': body.idempotencyKey },
            onResponse: (fields) => attempt.bind(fields),
            body,
          }),
          'checkout creation',
        ),
      );
      if (requestId !== activeRequest) return false;
      if (CHECKOUT_TERMINAL_STATES.includes(data?.status)) {
        checkoutRefunded = data.status === 'REFUNDED';
        retireCheckoutIntent(idempotencyKey);
        throw new Error(
          checkoutFailureMessage(
            data?.status,
            'Checkout has ended. Please try again.',
          ),
        );
      }
      const url = getAirwallexPaymentUrl(data);
      if (!/^https:\/\//i.test(url))
        throw new Error('Airwallex payment URL is unavailable.');
      const checkoutId = data?.id || data?.checkoutId;
      attempt.bind({ checkoutId, environment: isTestMode() ? 'demo' : 'prod' });
      await Promise.all([
        trackCheckoutMonitor('membership_popup_checkout_success', {
          checkoutId,
        }),
        trackCheckoutMonitor('membership_checkout_redirect', {
          checkoutId,
          action: context.action_type === 'upgrade' ? 'upgrade' : 'buy',
          plan: body.plan,
          billingCycle: body.billingCycle,
        }),
        attempt.finish('redirect'),
      ]);
      if (requestId !== activeRequest) return false;
      // Checkout 已创建且即将跳转：此时清除购买意图，刷新由幂等回放兜底。
      clearPurchaseIntent();
      if (checkoutId) {
        try {
          sessionStorage.setItem(SUCCESS_CHECKOUT_ID_STORAGE_KEY, checkoutId);
        } catch {}
      }
      window.zlog?.flushTracking?.();
      window.location.href = url;
      return true;
    } catch (error) {
      if (error?.name === 'AbortError' || requestId !== activeRequest)
        return false;
      attempt.finish('error');
      trackCheckoutMonitor('membership_popup_checkout_fail', {
        message: error?.message,
      });
      // HOSTED 跳转前弹层可能尚未打开；出错时先打开弹层再展示错误，否则结果写入隐藏节点。
      openLayer();
      showCheckoutRequestError(error);
      if (checkoutRefunded) {
        showRefundedCheckout();
      } else if (!keepCheckoutError) {
        resultDialog.hidden = true;
        offer.hidden = false;
        checkoutState = 'closed';
        root.dataset.checkoutState = 'closed';
      }
      return false;
    }
  }

  root.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('[data-jjb-membership-checkout-close]')) {
      closeLayer();
      return;
    }
    if (target?.closest('[data-jjb-membership-checkout-retry]')) {
      void handleCheckoutRetryClick();
    }
  });

  async function handleCheckoutRetryClick() {
    if (checkoutState === 'confirm_timeout') {
      const checkoutId = getStoredCheckoutId();
      if (checkoutId) {
        activeRequest += 1;
        setCheckoutState(
          'confirming',
          '<div><span class="jjb-membership-spinner" aria-hidden="true"></span><p>Checking payment status...</p></div>',
        );
        pollingStartedAt = 0;
        pollCheckout(checkoutId, activeRequest, true);
      }
      return;
    }
    setCheckoutRetryLoading(true);
    await redirectToAirwallexCheckout({ keepCheckoutError: true });
    setCheckoutRetryLoading(false);
  }
  resultRetry.addEventListener('click', async () => {
    if (checkoutState === 'confirm_timeout') {
      const checkoutId = getStoredCheckoutId();
      if (checkoutId) {
        // 只读失败尚不代表已提交；再次读取原会话。
        if (
          getStoredCheckout()?.pendingConfirmation !== true &&
          !isSetupCheckout()
        ) {
          await restoreCheckout(checkoutId);
          return;
        }
        activeRequest += 1;
        checkoutState = 'confirming';
        root.dataset.checkoutState = checkoutState;
        showResult('processing', processingMessage());
        pollingStartedAt = 0;
        pollCheckout(checkoutId, activeRequest, true);
      }
      return;
    }
    if (checkoutState === 'payment_failed') {
      if (isGiftResume()) context.signupId = null;
      if (!isSetupCheckout())
        trackMembership(
          'membership_pay_retry',
          getAirwallexErrorParams(latestPaymentError),
          { monitor: true },
        );
      setResultRetryLoading(true);
      await redirectToAirwallexCheckout({ keepCheckoutError: true });
      setResultRetryLoading(false);
      return;
    }
    if (!isSetupCheckout()) {
      setStoredCheckoutId('');
      idempotencyKey = '';
    }
    checkoutAttempt = undefined;
    checkoutState = 'closed';
    resultDialog.hidden = true;
    initializeCheckout();
  });
  window.addEventListener('resize', syncView);
  document.addEventListener('keydown', (event) => {
    if (root.hidden) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeLayer();
      return;
    }
    if (event.key !== 'Tab') return;
    const dialog = visibleDialog();
    const items = Array.from(
      dialog?.querySelectorAll(
        'button:not([disabled]), a[href], input, [tabindex="0"]',
      ) || [],
    );
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  const returnParams = new URLSearchParams(window.location.search);
  const returnedCheckoutId =
    !designMode && isLoggedIn() && returnParams.get('checkout') === 'success'
      ? returnParams.get('checkoutId')
      : '';
  const storedCheckoutId = designMode
    ? null
    : returnedCheckoutId || getStoredCheckoutId();
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted || root.hidden || !context || !isLoggedIn()) return;
    if (checkoutState === 'payment_success') {
      // 返回缓存保留的是历史付款成功，不代表离开期间会员身份没有变化。
      if (!isSetupCheckout()) {
        showResult(
          'success',
          'This payment was confirmed. Your current membership details will refresh.',
        );
        window.dispatchEvent(new CustomEvent('jjb:membership-updated'));
      }
      return;
    }
    const checkoutId = getStoredCheckoutId();
    if (checkoutId) {
      clearPolling();
      void restoreCheckout(checkoutId);
    }
  });
  if (storedCheckoutId) {
    context = {
      entry_page: 'direct',
      plan_type: 'pro',
      billing_cycle: 'monthly',
      plan_label: 'Membership',
      action_type: 'purchase',
    };
    // 未提交的换卡是否处于确认中（已提交）：外部回跳按已提交承接结果。
    let storedSnapshot = null;
    try {
      const cached = JSON.parse(
        sessionStorage.getItem(CHECKOUT_ID_STORAGE_KEY),
      );
      if (!returnedCheckoutId || cached?.checkoutId === returnedCheckoutId) {
        context = { ...context, ...cached?.context };
        idempotencyKey = cached?.idempotencyKey || '';
      }
      storedSnapshot = cached;
    } catch {}
    if (returnedCheckoutId) {
      // 外部验证回跳可以跨标签或丢失存储，先以原 ID 查询服务端，不推断付款目的。
      setStoredCheckoutId(storedCheckoutId, true);
      returnParams.delete('checkout');
      returnParams.delete('checkoutId');
      const query = returnParams.toString();
      window.history.replaceState(
        window.history.state,
        '',
        window.location.pathname +
          (query ? '?' + query : '') +
          window.location.hash,
      );
    }
    // 赠转付与未提交的换卡/购买不预开弹层：未支付会话的自动恢复会静默清理，
    // 预开只会闪现壳再关掉。已提交确认中/外部回跳保持开层承接结果。
    const autoConfirming =
      Boolean(returnedCheckoutId) ||
      storedSnapshot?.pendingConfirmation === true;
    if (autoConfirming) openLayer();
    restoreCheckout(storedCheckoutId);
  } else if (isLoggedIn()) {
    let intent;
    let purchaseIntentRaw = null;
    let purchaseIntentRead = false;
    try {
      purchaseIntentRaw = sessionStorage.getItem(PURCHASE_INTENT_STORAGE_KEY);
      purchaseIntentRead = true;
      intent = parsePurchaseIntent(purchaseIntentRaw);
    } catch {}
    if (intent && !isActivePaidMembership(getThemeMembershipState())) {
      // 意图先保留：Checkout 成功创建或用户显式关闭时才清除，
      // 中途失败/超时/刷新后刷新页面仍可安全重试。
      open({
        entry_page: intent.entry_page,
        plan_type: 'pro',
        billing_cycle: 'monthly',
      });
      setCtaLoading(true);
      void mergeAnonymousActivityAfterLogin();
      void startCheckout();
    } else if (intent || (purchaseIntentRead && purchaseIntentRaw !== null)) {
      // 已是付费会员，或意图缺失/过期/损坏时，不恢复购买并清理终态缓存。
      clearPurchaseIntent();
    }
  }

  return {
    open,
    openCheckout,
    prewarm,
    close: closeLayer,
    designMode,
    hasOpened: () => hasOpened,
    canAutoOpen: () =>
      !hasOpened &&
      (!popupSectionId || shouldShowPopup(popupSectionId, popupDelayDays)),
  };
}

function recordExperimentGroup(
  experimentName,
  key,
  value,
  groupIds,
  unknownGroupId,
) {
  const groupId = groupIds[String(value)] ?? unknownGroupId;
  window.zlog?.recordExperimentGroup?.(`${experimentName}:${key}`, groupId);
}

function getPopupSection() {
  return (
    document.querySelector('.newsletter-popup--original') ||
    document.querySelector('.newsletter-popup--v2')
  );
}

function getDelaySeconds(el) {
  return (parseInt(el.dataset.delaySeconds, 10) || 5) * 1000;
}

function getDelayDays(el) {
  return parseInt(el.dataset.delayDays, 10) || 30;
}

function shouldShowPopup(sectionId, delayDays) {
  try {
    const key = STORAGE_KEY_PREFIX + sectionId;
    const lastClosed = localStorage.getItem(key);
    if (!lastClosed) return true;
    const daysSinceClosed =
      (Date.now() - parseInt(lastClosed, 10)) / (1000 * 60 * 60 * 24);
    return daysSinceClosed >= delayDays;
  } catch {
    return true;
  }
}

function markPopupClosed(sectionId) {
  try {
    const key = STORAGE_KEY_PREFIX + sectionId;
    localStorage.setItem(key, String(Date.now()));
  } catch {}
}

function showOriginalPopup(sectionEl) {
  const popup =
    sectionEl.closest('.newsletter-popup--original') ||
    document.querySelector('.newsletter-popup--original');
  if (!popup) return;

  const sectionId = popup.dataset.sectionId;
  const delayDays = getDelayDays(popup);
  const delayMs = getDelaySeconds(popup);

  popup.querySelectorAll('.js-modal-close').forEach((btn) => {
    btn.addEventListener('click', () => {
      popup.classList.remove('modal--is-active');
      popup.style.display = 'none';
      markPopupClosed(sectionId);
      showOriginalReminder();
    });
  });

  initOriginalReminder(popup);

  if (!shouldShowPopup(sectionId, delayDays)) {
    showOriginalReminder();
    return;
  }

  setTimeout(() => {
    popup.style.display = '';
    popup.classList.add('modal--is-active');
  }, delayMs);
}

function initOriginalReminder(popup) {
  const wrapper = document.querySelector('.newsletter-reminder--original');
  if (!wrapper) return;

  const reminder = wrapper.querySelector('newsletter-reminder');
  if (reminder) {
    reminder.addEventListener('click', () => {
      wrapper.style.display = 'none';
      if (popup) {
        popup.style.display = '';
        popup.classList.add('modal--is-active');
      }
    });
  }
}

function showOriginalReminder() {
  const wrapper = document.querySelector('.newsletter-reminder--original');
  if (!wrapper) return;
  wrapper.style.display = '';
  const reminder = wrapper.querySelector('newsletter-reminder');
  if (reminder) {
    reminder.dataset.enabled = 'true';
  }
}

function showV2Popup(hideGiftBox = false) {
  const popup = document.querySelector('.newsletter-popup--v2');
  if (!popup) return;

  const sectionId = popup.dataset.sectionId;
  const delayDays = getDelayDays(popup);
  const delayMs = getDelaySeconds(popup);

  const closePopup = () => {
    popup.style.display = 'none';
    markPopupClosed(sectionId);
    if (!hideGiftBox) {
      showV2Reminder();
    }
  };

  popup.addEventListener('click', (e) => {
    if (
      e.target.closest('.newsletter-popup-v2__close') ||
      e.target.closest('.newsletter-popup-v2__overlay')
    ) {
      closePopup();
    }
  });

  if (!hideGiftBox) {
    initV2Reminder(popup);
  }

  if (popup.querySelector('.newsletter-v2-form-success')) {
    markPopupClosed(sectionId);
    if (window.toast) window.toast('🎁 Thanks for subscribing！');
    return;
  }

  if (popup.querySelector('.newsletter-v2-form-error')) {
    if (window.toast) window.toast('Login failed. Please try again.');
    return;
  }

  if (!shouldShowPopup(sectionId, delayDays)) {
    if (!hideGiftBox) {
      showV2Reminder();
    }
    return;
  }

  // 命中 hide_gift_box 实验组时立即展示，否则按配置延时展示
  if (hideGiftBox) {
    popup.style.display = 'block';
  } else {
    setTimeout(() => {
      popup.style.display = 'block';
    }, delayMs);
  }
}

function initV2Reminder(popup) {
  const wrapper = document.querySelector('.newsletter-reminder--v2');
  if (!wrapper) return;

  const reminder = wrapper.querySelector('newsletter-reminder');
  if (reminder) {
    reminder.addEventListener('click', () => {
      if (reminder._wasDragged) {
        reminder._wasDragged = false;
        return;
      }
      wrapper.style.display = 'none';
      if (popup) popup.style.display = 'block';
    });
  }

  const closeBtn = wrapper.querySelector('.newsletter-reminder-v2__close');
  if (closeBtn) {
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      wrapper.style.display = 'none';
    });
  }
}

function showV2Reminder() {
  const wrapper = document.querySelector('.newsletter-reminder--v2');
  if (!wrapper) return;
  wrapper.style.display = '';
  const reminder = wrapper.querySelector('newsletter-reminder');
  if (reminder) {
    reminder.dataset.enabled = 'true';
  }
}

async function initPopupAbTest() {
  const membershipPopup = createMembershipPopupController();
  if (location.pathname === '/pages/vip-program') return;
  if (membershipPopup) window.jjbMembershipPopup = membershipPopup;
  const sectionEl = getPopupSection();
  const canCheckGenerationCount = () =>
    Boolean(
      membershipPopup &&
      membershipPopup.designMode !== true &&
      membershipPopup.canAutoOpen() &&
      window.jjbMembershipCheckoutPending !== true &&
      !isActivePaidMembership(getThemeMembershipState()),
    );
  const generationCountPromise = canCheckGenerationCount()
    ? hasRequiredGenerationCountForAutoOpen()
    : Promise.resolve(false);

  if (
    canCheckGenerationCount() &&
    (await generationCountPromise) &&
    canCheckGenerationCount()
  ) {
    membershipPopup.open({ entry_page: 'launch' });
    return;
  }

  if (
    membershipPopup?.hasOpened() ||
    window.jjbMembershipCheckoutPending === true
  )
    return;
  if (!sectionEl) return;

  const group = 'new';
  const hideGiftBox = true;
  // try {
  //   const experiment = await getExperiment(EXPERIMENT_NAME);
  //   group = experiment.get('mode', 'old');
  //   recordExperimentGroup(
  //     EXPERIMENT_NAME,
  //     'mode',
  //     group,
  //     PROMOTION_POPUP_GROUP_IDS,
  //     34,
  //   );

  //   // 读取 hide_gift_box 实验：Control=false，Test=true，异常默认 false
  //   try {
  //     const hideGiftBoxExp = await getExperiment(HIDE_GIFT_BOX_EXPERIMENT_NAME);
  //     hideGiftBox = hideGiftBoxExp.get('enable', false) === true;
  //     recordExperimentGroup(
  //       HIDE_GIFT_BOX_EXPERIMENT_NAME,
  //       'enable',
  //       hideGiftBox,
  //       HIDE_GIFT_BOX_GROUP_IDS,
  //       37,
  //     );
  //   } catch {
  //     hideGiftBox = false;
  //   }
  // } catch {
  //   group = 'old';
  //   hideGiftBox = false;
  // }

  logEvent('promotion_popup_exposure', null, {
    group,
    hide_gift_box: hideGiftBox,
  });

  // if (group === 'none') {
  //   return;
  // }

  if (group === 'new') {
    showV2Popup(hideGiftBox);
  } else {
    showOriginalPopup(sectionEl);
  }
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  initPopupAbTest();
}
